import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { sanitizeText } from 'utils';
import {
    Button,
    HorizontalLayout,
    IconLightbulb,
    IconPlus,
    IconSearch,
    SearchInput,
    Spinner,
    Text,
    VerticalLayout,
} from 'widgets';

export const MIN_SEARCH_LENGTH = 2;

export interface ResourceSearchComponentProps<DataType extends object> {
    data: DataType;
}

interface ResourceSearchProps<DataType extends object> {
    searchPlaceholder: string;
    data: DataType[];
    suggestions?: DataType[];
    searchFn: (pattern: RegExp, item: DataType) => boolean;
    selectionCheckFn: (item: DataType) => boolean;
    Component: (props: ResourceSearchComponentProps<DataType>) => JSX.Element;
    onClick: (item: DataType) => void;
    isLoading?: boolean;
}

export const ResourceSearch = <T extends object>({
    searchPlaceholder,
    data = [],
    suggestions = [],
    searchFn,
    selectionCheckFn,
    Component,
    onClick,
    isLoading,
}: ResourceSearchProps<T>) => {
    const [selectedTab, setSelectedTab] = useState<'suggestions' | 'search'>('suggestions');
    const [searchText, setSearchText] = useState('');

    const onChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(sanitizeText(target.value));
    };

    const searchResults = useMemo(() => {
        const array = selectedTab === 'suggestions' ? suggestions : data;

        if (!searchText) {
            return array;
        }

        const pattern = new RegExp(searchText, 'gi');
        return array.filter((item: any) => searchFn(pattern, item));
    }, [data, searchFn, searchText, selectedTab, suggestions]);

    return (
        <VerticalLayout $overflow="hidden" $padding="16px 0 0 0">
            <Tabs $gap={16}>
                <Tab
                    $gap={4}
                    $rowCenter
                    onClick={() => setSelectedTab('suggestions')}
                    $active={selectedTab === 'suggestions'}
                >
                    <IconLightbulb size="xSmall" />
                    <Text largeBody>Suggestions</Text>
                </Tab>

                <Tab $gap={4} $rowCenter onClick={() => setSelectedTab('search')} $active={selectedTab === 'search'}>
                    <IconSearch size="micro" />
                    <Text largeBody>Search</Text>
                </Tab>
            </Tabs>
            <SearchField>
                <SearchInput value={searchText} onChange={onChange} placeholder={searchPlaceholder} />
            </SearchField>
            {isLoading ? (
                <ResultItem $center>
                    <Spinner />
                </ResultItem>
            ) : (
                <VerticalLayout $overflow="scroll">
                    {searchResults.slice(0, 50).map((result, idx) => (
                        <ResultItem key={idx} $rowCenter $apart>
                            <Component data={result} />
                            {selectionCheckFn(result) ? (
                                <Text $themeColor="highlight">
                                    <HorizontalLayout $gap={4} $rowCenter $padding="0 16px 0 0">
                                        Added
                                    </HorizontalLayout>
                                </Text>
                            ) : (
                                <Button
                                    buttonType="ghost"
                                    size="small"
                                    onClick={() => onClick(result)}
                                    icon={<IconPlus size="tiny" />}
                                >
                                    Add
                                </Button>
                            )}
                        </ResultItem>
                    ))}
                    {searchResults.length >= 50 && (
                        <ResultItem>
                            <Text>Showing first 50 results...</Text>
                        </ResultItem>
                    )}
                </VerticalLayout>
            )}
        </VerticalLayout>
    );
};

const Tabs = styled(HorizontalLayout)`
    padding: 0 24px;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};
`;

const Tab = styled(HorizontalLayout)<{ $active: boolean }>`
    padding: 12px 8px;
    border-bottom: 3px solid;
    text-decoration: none;
    cursor: pointer;
    border-color: ${({ $active, theme }) => ($active ? theme.border.active : 'transparent')};
    color: ${({ $active, theme }) => ($active ? theme.text.primary : theme.text.subtle)};
`;

const SearchField = styled.div`
    padding: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};
`;

const ResultItem = styled(HorizontalLayout)`
    padding: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};
`;
